import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { useCommentsGateway } from './CommentsGatewayProvider';
import { getCommentsV2QueryOptions } from '../api/queryOptions';

import { useUserScopedKey, useUserScopedQuery } from '@/hooks/useUserScopedQuery';

/**
 * A post's thread, and a way to read when it was last updated at the moment
 * of asking. The post screen compares that time after refreshing the post,
 * which can bring the comments with it, so it must read the cache live rather
 * than the value captured at render.
 */
export function useComments(postId: number, { enabled }: { enabled: boolean }) {
  const queryClient = useQueryClient();

  const commentsKey = useUserScopedKey(getCommentsV2QueryOptions);

  const commentsQuery = useUserScopedQuery(
    getCommentsV2QueryOptions,
    [postId, useCommentsGateway()],
    { enabled }
  );

  const readCommentsUpdatedAt = useCallback(
    () => queryClient.getQueryState(commentsKey(postId))?.dataUpdatedAt ?? 0,
    [queryClient, commentsKey, postId]
  );

  return { commentsQuery, readCommentsUpdatedAt };
}
